import React, {useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';


import {clearSelection} from 'src/components/data-table-selection';
import {AccountNameFilter} from 'src/components/filters';
import {AccountSortField} from 'src/openapi/admin/models/AccountSortField';
import {SortDirection as SortDirections} from 'src/openapi/admin/models/SortDirection';
import {getMainColumnHeader} from 'src/utils/getMainColumnHeader';

import {SortHeaderFilter} from '@pb/kit-component-filters';
import {LocaleContext} from '@pb/kit-context-locale';

import {COLUMNS, HEADERS, SELECTION_KEY, SORTING_ERROR} from '../../constants';
import {
    accountSearchResultStateActions,
    accountSearchResultStateSelector,
} from '../../slices/account-search-result-state.slice';
import {
    accountSearchResultDataSelector,
    accountSearchResultResultSelector,
} from '../../slices/account-search-result.slice';

import type {SortDirection} from 'src/openapi/admin/models/SortDirection';


export const AccountNameHeader = () => {
    const dispatch = useDispatch();
    const cacheKeys = useSelector(accountSearchResultStateSelector);

    const {loadingError} = useSelector(accountSearchResultDataSelector);
    const result = useSelector(accountSearchResultResultSelector);
    const {locale} = useContext(LocaleContext);

    const [accountName, setAccountName] = useState(cacheKeys.filters.accountName as string | undefined);

    const sort = useCallback((direction: ('ASC' | 'DESC')) => {
        dispatch(clearSelection(SELECTION_KEY));
        dispatch(accountSearchResultStateActions.setSortCriteria({
            direction: direction as SortDirection,
            field: AccountSortField.ACCOUNT_NAME,
        }));
    }, [dispatch]);

    const changeAccountName = useCallback((newName: string) => {
        setAccountName(newName || undefined);
    }, [setAccountName]);

    const applyFilter = useCallback(() => {
        const {accountName: _prevName, ...otherFilters} = cacheKeys.filters;

        dispatch(clearSelection(SELECTION_KEY));
        if (accountName) {
            dispatch(accountSearchResultStateActions.setFilters({
                ...otherFilters,
                accountName,
            }));
        } else {
            dispatch(accountSearchResultStateActions.setFilters(otherFilters));
        }
    }, [accountName, dispatch, cacheKeys.filters]);

    const clearFilter = useCallback(() => {
        const {accountName: _prevName, ...otherFilters} = cacheKeys.filters;

        dispatch(clearSelection(SELECTION_KEY));
        dispatch(accountSearchResultStateActions.setFilters(otherFilters));
    }, [dispatch, cacheKeys.filters]);

    const cancelFilter = useCallback(() => {
        setAccountName(cacheKeys.filters.accountName as string | undefined);
    }, [cacheKeys.filters.accountName]);

    useEffect(() => {
        if (loadingError?.message === SORTING_ERROR) {
            sort(SortDirections.ASC);
        }
    }, [loadingError, sort]);

    useEffect(() => {
        setAccountName(cacheKeys.filters.accountName as string | undefined);
    }, [cacheKeys.filters.accountName]);

    const caption = useMemo(() => (
        getMainColumnHeader(HEADERS[COLUMNS.ACCOUNT_NAME], locale, result?.totalCount)
    ), [result?.totalCount, locale]);

    return (
        <SortHeaderFilter
            filterActive={!!cacheKeys.filters?.accountName}
            sortable={true}
            caption={caption}
            direction={cacheKeys.sortCriteria.field === AccountSortField.ACCOUNT_NAME
                ? cacheKeys.sortCriteria.direction
                : undefined}
            defaultDirection={SortDirections.ASC}
            onSort={sort}
            onApply={applyFilter}
            onReset={clearFilter}
            onCancel={cancelFilter}>
            <AccountNameFilter accountName={accountName} onChange={changeAccountName}/>
        </SortHeaderFilter>
    );
};
